import { addDays } from '../lib/reporting';
import { useReport } from './ReportContext';

/**
 * Shared report filter bar: date range (+ quick presets anchored to the latest
 * available data date), team region, store and driver, plus an export button.
 * Presets are anchored to the data's max date rather than "today" because the
 * source is a historical sync — anchoring to today would yield empty ranges.
 */
export function FilterBar({ onExport }: { onExport?: () => void }) {
  const { options, filters, setFilters } = useReport();
  const max = options?.dataMax ?? undefined;
  const min = options?.dataMin ?? undefined;

  const preset = (days: number) => {
    if (!max) return;
    setFilters({ to: max, from: addDays(max, -(days - 1)) });
  };

  return (
    <div className="filter-card">
      <div className="filter-grid">
        <div className="filter-block">
          <span className="filter-label">Date range</span>
          <div className="filter-dates">
            <input
              type="date"
              value={filters.from}
              min={min}
              max={filters.to || max}
              onChange={(e) => setFilters({ from: e.target.value })}
            />
            <span aria-hidden="true">→</span>
            <input
              type="date"
              value={filters.to}
              min={filters.from || min}
              max={max}
              onChange={(e) => setFilters({ to: e.target.value })}
            />
          </div>
          <div className="filter-presets">
            <button type="button" onClick={() => preset(1)}>Last day</button>
            <button type="button" onClick={() => preset(7)}>Last 7 days</button>
            <button type="button" onClick={() => preset(30)}>Last 30 days</button>
          </div>
        </div>

        <label className="filter-block">
          <span className="filter-label">Team region</span>
          <select value={filters.region} onChange={(e) => setFilters({ region: e.target.value })}>
            <option value="">All regions</option>
            {options?.regions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>

        <label className="filter-block">
          <span className="filter-label">Store</span>
          <select value={filters.store} onChange={(e) => setFilters({ store: e.target.value })}>
            <option value="">All stores</option>
            {options?.stores.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </select>
        </label>

        <label className="filter-block">
          <span className="filter-label">Driver</span>
          <select value={filters.driver} onChange={(e) => setFilters({ driver: e.target.value })}>
            <option value="">All drivers</option>
            {options?.drivers.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
      </div>

      {onExport && (
        <div className="filter-actions">
          <button type="button" className="export-btn" onClick={onExport}>
            ↓ Export
          </button>
        </div>
      )}
    </div>
  );
}
